import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { IconDeviceMobileDown, IconX } from '@tabler/icons-react'
import Button from './ui/Button'
import { useIsMobile } from '../hooks/useIsMobile'
import { getInstallPrompt, onInstallPromptChange, clearInstallPrompt } from '../utils/installPrompt'

// Subtle "add to home screen" banner, shown on mobile once the browser signals
// the app is installable (beforeinstallprompt). Dismiss is remembered for 7 days
// so it doesn't nag; installing (or an already-installed app) hides it for good.
const DISMISS_KEY = 'tg_install_dismissed_at'
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000

function recentlyDismissed() {
  const ts = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10)
  return ts > 0 && Date.now() - ts < SEVEN_DAYS_MS
}

function isStandalone() {
  return (
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    window.navigator.standalone === true // iOS Safari
  )
}

export default function InstallPrompt() {
  const { t } = useTranslation()
  const mobile = useIsMobile()
  const [prompt, setPrompt] = useState(getInstallPrompt())

  useEffect(() => onInstallPromptChange(setPrompt), [])

  if (!mobile || !prompt || isStandalone() || recentlyDismissed()) return null

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, String(Date.now()))
    setPrompt(null)
  }

  async function install() {
    try {
      prompt.prompt()
      await prompt.userChoice
    } catch {
      /* user closed the sheet — nothing to do */
    }
    clearInstallPrompt()
    setPrompt(null)
  }

  return (
    <div
      role="dialog"
      aria-label={t('install.title')}
      style={{
        display: 'flex', alignItems: 'center', gap: 'var(--tg-space-3)',
        background: 'var(--tg-bg-surface, #fff)',
        border: '1px solid var(--tg-border-default, #e5e7eb)',
        borderRadius: 'var(--tg-radius-lg)',
        boxShadow: 'var(--tg-shadow-sm, 0 1px 3px rgba(0,0,0,0.08))',
        padding: '12px 14px',
        marginBottom: 'var(--tg-space-3)',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          flexShrink: 0, width: 34, height: 34, borderRadius: 8,
          background: 'var(--tg-bg-accent-subtle, #ECFDFA)', color: 'var(--tg-teal-600, #0E9B8C)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <IconDeviceMobileDown size={20} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 'var(--tg-text-sm, 14px)', fontWeight: 600, color: 'var(--tg-text-primary, #111)' }}>
          {t('install.title')}
        </div>
        <div style={{ fontSize: 'var(--tg-text-xs, 12px)', color: 'var(--tg-text-secondary, #666)', lineHeight: 1.4 }}>
          {t('install.body')}
        </div>
      </div>
      <Button variant="primary" size="sm" onClick={install}>
        {t('install.action')}
      </Button>
      <button
        onClick={dismiss}
        aria-label={t('install.dismiss')}
        style={{ background: 'none', border: 'none', color: 'var(--tg-text-secondary, #888)', cursor: 'pointer', padding: 2, lineHeight: 1 }}
      >
        <IconX size={16} />
      </button>
    </div>
  )
}
